import React, { ReactNode, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { StudyDataFilterContextProvider } from '@contexts-clean/study-data-filter-context';
import { IDataset } from '@pages-clean/central-monitoring/interfaces/context';
import { useTranslation } from '@hooks/index';
import { javascriptDateWithoutTimeFromString } from '@utils/convertor';
import { GET_STUDY_DASHBOARD_CONFIGURATION } from '@queries/central-monitoring';
import {
    studyDashboardConfigForStudy,
    studyDashboardConfigForStudyVariables,
} from '@queries/generated/central-monitoring.generated';
import { LoggedInPageProps } from '@config/routes/interfaces';

export interface StudySiteHubContentProps {
    studyRef: string;
    dataset: IDataset;
    studyDashboardConfig: studyDashboardConfigForStudy;
}

interface StudySiteHubWrapperProps extends LoggedInPageProps {
    Content: ({ studyRef, dataset, studyDashboardConfig }: StudySiteHubContentProps) => ReactNode;
}

const StudySiteHubWrapper = ({ study, cm2Dataset: dataset, Content }: StudySiteHubWrapperProps) => {
    const { t } = useTranslation('cm');
    const studyRef = study?.id as string;

    const [loadConfig, { data: studyConfigData, loading: studyConfigLoading, called: studyConfigCalled }] =
        useLazyQuery<studyDashboardConfigForStudy, studyDashboardConfigForStudyVariables>(
            GET_STUDY_DASHBOARD_CONFIGURATION,
        );

    const params = new URLSearchParams(window.location.search);
    const copySessionId = params.get('copySessionId') ?? undefined;

    useEffect(() => {
        loadConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef]);

    if (studyConfigLoading || !studyConfigCalled) {
        return <Loading />;
    }

    if (!studyConfigData) {
        return <ErrorMessage error={t('studyHubPage.unableToFetch')} />;
    }
    return (
        <StudyDataFilterContextProvider
            studyRef={studyRef}
            datasetId={dataset?.id as number}
            preAppliedFilters={{
                datasetId: undefined,
                filters: [],
            }}
            copySessionId={copySessionId}
        >
            <Content
                studyRef={studyRef}
                dataset={{
                    id: dataset?.id as number,
                    date: javascriptDateWithoutTimeFromString(dataset!.dataCutDate!)!,
                }}
                studyDashboardConfig={studyConfigData}
            />
        </StudyDataFilterContextProvider>
    );
};

export default StudySiteHubWrapper;
